import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, FileText, ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

// Touch drag state interface
interface TouchDragState {
  isDragging: boolean;
  startIndex: number | null;
  currentX: number;
  currentY: number;
  startX: number;
  startY: number;
}

interface ImageReviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  uploadedImages: File[];
  selectedImageOrder: number[];
  setSelectedImageOrder: React.Dispatch<React.SetStateAction<number[]>>;
  draggedIndex: number | null;
  setDraggedIndex: React.Dispatch<React.SetStateAction<number | null>>;
  dragOverIndex: number | null;
  setDragOverIndex: React.Dispatch<React.SetStateAction<number | null>>;
  setUploadedImages: React.Dispatch<React.SetStateAction<File[]>>;
  convertImagesToPdf: (images: File[]) => Promise<File>;
  onUploadPdf: (pdf: File) => void;
}

const ImageReviewModal = ({
  open,
  onOpenChange,
  uploadedImages,
  selectedImageOrder,
  setSelectedImageOrder,
  draggedIndex,
  setDraggedIndex,
  dragOverIndex,
  setDragOverIndex,
  setUploadedImages,
  convertImagesToPdf,
  onUploadPdf,
}: ImageReviewModalProps) => {
  const [isConvertingToPdf, setIsConvertingToPdf] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);

  // Image preview state
  const [previewImageIndex, setPreviewImageIndex] = useState<number | null>(null);

  // Preview handlers - back button goes back to grid view
  const handleClosePreview = () => {
    setPreviewImageIndex(null);
  };
  const handlePrevPreview = () => setPreviewImageIndex(prev => prev !== null && prev > 0 ? prev - 1 : uploadedImages.length - 1);
  const handleNextPreview = () => setPreviewImageIndex(prev => prev !== null && prev < uploadedImages.length - 1 ? prev + 1 : 0);

  // PDF Preview mode
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  // Touch drag state for mobile
  const [touchDrag, setTouchDrag] = useState<TouchDragState>({
    isDragging: false,
    startIndex: null,
    currentX: 0,
    currentY: 0,
    startX: 0,
    startY: 0,
  });
  const gridRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleImageDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    setHasDragged(false);
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    if (e.currentTarget) {
      e.dataTransfer.setDragImage(e.currentTarget, e.currentTarget.offsetWidth / 2, e.currentTarget.offsetHeight / 2);
    }
  };

  const handleImageDrag = () => {
    setHasDragged(true);
  };

  const handleImageDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleImageDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOverIndex(null);
  };

  const handleImageDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    const dragIdx = draggedIndex ?? parseInt(e.dataTransfer.getData('text/plain'));

    if (dragIdx === null || isNaN(dragIdx) || dragIdx === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    setUploadedImages(prev => {
      const newImages = [...prev];
      const draggedImage = newImages[dragIdx];
      const dropImage = newImages[dropIndex];
      newImages[dropIndex] = draggedImage;
      newImages[dragIdx] = dropImage;
      return newImages;
    });

    // Update selectedImageOrder: swap the indices AND sort by visual order
    setSelectedImageOrder(prev => {
      // First swap the indices to match the new image positions
      const swapped = prev.map(idx => {
        if (idx === dragIdx) return dropIndex;
        if (idx === dropIndex) return dragIdx;
        return idx;
      });
      // Then sort by index to keep numbering in visual order (1, 2, 3 from left to right)
      return [...swapped].sort((a, b) => a - b);
    });

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleImageDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Touch event handlers for mobile drag and drop
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>, index: number) => {
    const touch = e.touches[0];
    setTouchDrag({
      isDragging: false, // Will set to true on move
      startIndex: index,
      currentX: touch.clientX,
      currentY: touch.clientY,
      startX: touch.clientX,
      startY: touch.clientY,
    });
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (touchDrag.startIndex === null) return;

    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchDrag.startX);
    const deltaY = Math.abs(touch.clientY - touchDrag.startY);

    // Only start dragging if moved more than 10px (to differentiate from tap)
    if (deltaX > 10 || deltaY > 10) {
      e.preventDefault(); // Prevent scrolling while dragging
      setHasDragged(true);
      setDraggedIndex(touchDrag.startIndex);

      setTouchDrag(prev => ({
        ...prev,
        isDragging: true,
        currentX: touch.clientX,
        currentY: touch.clientY,
      }));

      // Find which element we're over
      const elementsUnder = document.elementsFromPoint(touch.clientX, touch.clientY);
      let foundIndex: number | null = null;

      for (const el of elementsUnder) {
        const idx = itemRefs.current.findIndex(ref => ref?.contains(el));
        if (idx !== -1 && idx !== touchDrag.startIndex) {
          foundIndex = idx;
          break;
        }
      }

      setDragOverIndex(foundIndex);
    }
  };

  const handleTouchEnd = () => {
    if (touchDrag.isDragging && touchDrag.startIndex !== null && dragOverIndex !== null) {
      // Perform the swap
      const dragIdx = touchDrag.startIndex;
      const dropIndex = dragOverIndex;

      setUploadedImages(prev => {
        const newImages = [...prev];
        const draggedImage = newImages[dragIdx];
        const dropImage = newImages[dropIndex];
        newImages[dropIndex] = draggedImage;
        newImages[dragIdx] = dropImage;
        return newImages;
      });

      setSelectedImageOrder(prev => {
        const swapped = prev.map(idx => {
          if (idx === dragIdx) return dropIndex;
          if (idx === dropIndex) return dragIdx;
          return idx;
        });
        return [...swapped].sort((a, b) => a - b);
      });
    }

    // Reset touch drag state
    setTouchDrag({
      isDragging: false,
      startIndex: null,
      currentX: 0,
      currentY: 0,
      startX: 0,
      startY: 0,
    });
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleImageClick = (idx: number) => {
    if (!hasDragged) {
      setSelectedImageOrder(prev => {
        const existingIndex = prev.indexOf(idx);
        if (existingIndex !== -1) {
          return prev.filter(i => i !== idx);
        } else {
          return [...prev, idx];
        }
      });
    }
    setHasDragged(false);
  };

  // Preview navigation
  const handlePreviewNav = (direction: 'prev' | 'next') => {
    if (previewImageIndex === null) return;
    if (direction === 'prev') {
      setPreviewImageIndex(prev => prev !== null && prev > 0 ? prev - 1 : uploadedImages.length - 1);
    } else {
      setPreviewImageIndex(prev => prev !== null && prev < uploadedImages.length - 1 ? prev + 1 : 0);
    }
  };

  // Keyboard navigation for preview
  useEffect(() => {
    if (previewImageIndex === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPreviewImageIndex(null);
      } else if (e.key === 'ArrowLeft') {
        handlePreviewNav('prev');
      } else if (e.key === 'ArrowRight') {
        handlePreviewNav('next');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewImageIndex, uploadedImages.length]);

  // Get selected images in order
  const getSelectedImages = () => {
    return selectedImageOrder
      .map(i => uploadedImages[i])
      .filter(Boolean) as File[];
  };

  // Handle Preview as PDF
  const handlePreviewAsPdf = () => {
    if (selectedImageOrder.length === 0) {
      toast.error("Please select at least one image");
      return;
    }
    setShowPdfPreview(true);
  };

  // Reset preview states when modal closes
  useEffect(() => {
    if (!open) {
      setShowPdfPreview(false);
      setPreviewImageIndex(null);
    }
  }, [open]);

  const selectedImages = getSelectedImages();

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="w-[95vw] max-w-4xl h-[95vh] sm:h-auto sm:max-h-[90vh] flex flex-col p-3 sm:p-6 rounded-xl sm:rounded-2xl overflow-hidden"
          hideClose
        >
          {previewImageIndex !== null ? (
            // Image Preview View (inside modal)
            <>
              <div className="flex items-center justify-between gap-2 mb-3 sm:mb-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <button
                    onClick={handleClosePreview}
                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors flex-shrink-0"
                  >
                    <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600" />
                  </button>
                  <DialogTitle className="text-lg sm:text-xl font-bold text-gray-900">Image Preview</DialogTitle>
                </div>
                <div className="text-xs text-gray-500 bg-gray-50 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full flex-shrink-0">
                  {previewImageIndex + 1} / {uploadedImages.length}
                </div>
              </div>

              {/* Image Preview Container */}
              <div className="flex-1 bg-gray-900 rounded-lg sm:rounded-xl relative flex flex-col min-h-0">
                {/* Navigation Buttons - Sticky */}
                {uploadedImages.length > 1 && (
                  <>
                    <button
                      onClick={handlePrevPreview}
                      className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 bg-white/20 hover:bg-white/40 active:bg-white/60 rounded-full flex items-center justify-center transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-white" strokeWidth={2.5} />
                    </button>
                    <button
                      onClick={handleNextPreview}
                      className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 bg-white/20 hover:bg-white/40 active:bg-white/60 rounded-full flex items-center justify-center transition-colors"
                    >
                      <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-white" strokeWidth={2.5} />
                    </button>
                  </>
                )}

                {/* Scrollable Image Container */}
                <div className="flex-1 overflow-auto p-4">
                  <img
                    src={URL.createObjectURL(uploadedImages[previewImageIndex])}
                    alt={`Preview ${previewImageIndex + 1}`}
                    className="w-full h-auto"
                    draggable={false}
                  />
                </div>
              </div>

              {/* Footer with file name */}
              <div className="pt-3 sm:pt-4 border-t border-gray-100 mt-3 sm:mt-4 flex items-center justify-between">
                <div className="text-xs sm:text-sm text-gray-500 truncate flex-1">
                  {uploadedImages[previewImageIndex].name}
                </div>
                <Button variant="outline" onClick={handleClosePreview} className="ml-3">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
              </div>
            </>
          ) : !showPdfPreview ? (
            // Image Selection View
            <>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3 sm:mb-4">
                <DialogTitle className="text-lg sm:text-xl font-bold text-gray-900">Review & Reorder Images</DialogTitle>
                <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full w-fit">
                  <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                  <span className="hidden sm:inline">Drag to reorder</span>
                  <span className="sm:hidden">Hold & drag</span>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto min-h-0 pr-1 sm:pr-2">
                <div ref={gridRef} className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                  {uploadedImages.map((img: File, idx: number) => {
                    // Ensure itemRefs array is the right size
                    if (itemRefs.current.length !== uploadedImages.length) {
                      itemRefs.current = uploadedImages.map(() => null);
                    }
                    const selectionIndex = selectedImageOrder.indexOf(idx);
                    const isSelected = selectionIndex !== -1;
                    const selectionOrder = selectionIndex + 1;
                    const isDragging = draggedIndex === idx;
                    const isDragOver = dragOverIndex === idx;

                    return (
                      <div
                        key={idx}
                        ref={(el) => { itemRefs.current[idx] = el; }}
                        draggable={true}
                        onDragStart={(e) => handleImageDragStart(e, idx)}
                        onDrag={handleImageDrag}
                        onDragOver={(e) => handleImageDragOver(e, idx)}
                        onDragLeave={handleImageDragLeave}
                        onDrop={(e) => handleImageDrop(e, idx)}
                        onDragEnd={handleImageDragEnd}
                        onTouchStart={(e) => handleTouchStart(e, idx)}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onClick={() => handleImageClick(idx)}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          setPreviewImageIndex(idx);
                        }}
                        className={`relative group rounded-xl overflow-hidden border-2 transition-all duration-200 cursor-grab active:cursor-grabbing select-none touch-none
                          ${isDragging ? 'opacity-40 scale-95 border-indigo-400 shadow-lg' : ''}
                          ${isDragOver ? 'border-indigo-500 ring-4 ring-indigo-500/30 scale-[1.02] bg-indigo-50' : ''}
                          ${!isDragging && !isDragOver && isSelected ? 'border-indigo-500 ring-2 ring-indigo-500/20' : ''}
                          ${!isDragging && !isDragOver && !isSelected ? 'border-gray-200 hover:border-gray-300 hover:shadow-md' : ''}
                        `}
                      >
                        {/* Drag Handle Indicator - Hidden on mobile */}
                        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none hidden sm:block">
                          <div className="bg-white/95 backdrop-blur-sm px-2.5 py-1.5 rounded-full shadow-md flex items-center gap-1.5 border border-gray-100">
                            <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                              <circle cx="9" cy="5" r="1.5" />
                              <circle cx="15" cy="5" r="1.5" />
                              <circle cx="9" cy="12" r="1.5" />
                              <circle cx="15" cy="12" r="1.5" />
                              <circle cx="9" cy="19" r="1.5" />
                              <circle cx="15" cy="19" r="1.5" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500">Drag</span>
                          </div>
                        </div>

                        <div className="aspect-[3/4] bg-gray-100 relative pointer-events-none">
                          <img
                            src={URL.createObjectURL(img)}
                            alt={`Page ${idx + 1}`}
                            className="w-full h-full object-contain"
                            draggable={false}
                          />

                          {/* Double-click hint - Hidden on mobile (touch devices use tap instead) */}
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block">
                            <div className="bg-black/70 backdrop-blur-sm px-2 py-1 rounded-md flex items-center gap-1">
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              <span className="text-[10px] text-white font-medium">Double-click to view</span>
                            </div>
                          </div>

                          {/* Selection Badge */}
                          <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 z-10">
                            <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center border-2 shadow-lg transition-all duration-200
                              ${isSelected
                                ? 'bg-gradient-to-br from-indigo-500 to-purple-600 border-white text-white scale-110'
                                : 'bg-white/80 border-gray-300 hover:border-gray-400'
                              }`}
                            >
                              {isSelected && <span className="text-xs sm:text-sm font-bold">{selectionOrder}</span>}
                            </div>
                          </div>

                          {/* Drop Indicator */}
                          {isDragOver && (
                            <div className="absolute inset-0 bg-indigo-500/30 flex items-center justify-center backdrop-blur-[1px]">
                              <div className="bg-white rounded-full p-3 shadow-xl">
                                <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                </svg>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="p-1.5 sm:p-2 bg-white text-[10px] sm:text-xs text-gray-500 truncate text-center font-medium border-t border-gray-100 pointer-events-none">
                          {img.name}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="pt-3 sm:pt-4 border-t border-gray-100 mt-3 sm:mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
                  {selectedImageOrder.length} of {uploadedImages.length} images selected
                </div>
                <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                  <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
                    Cancel
                  </Button>
                  <Button
                    onClick={handlePreviewAsPdf}
                    className="bg-indigo-600 text-white hover:bg-indigo-700 w-full sm:w-auto sm:min-w-[140px]"
                    disabled={selectedImageOrder.length === 0}
                  >
                    <span className="flex items-center justify-center gap-2">
                      Preview as PDF <FileText className="w-4 h-4" />
                    </span>
                  </Button>
                </div>
              </div>
            </>
          ) : (
            // PDF Preview View
            <>
              <div className="flex items-center justify-between gap-2 mb-3 sm:mb-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <button
                    onClick={() => setShowPdfPreview(false)}
                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors flex-shrink-0"
                  >
                    <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600" />
                  </button>
                  <DialogTitle className="text-lg sm:text-xl font-bold text-gray-900">PDF Preview</DialogTitle>
                </div>
                <div className="text-xs text-gray-500 bg-gray-50 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full flex-shrink-0">
                  {selectedImages.length} pages
                </div>
              </div>

              {/* PDF Pages - Scrollable Container */}
              <div className="flex-1 overflow-y-auto min-h-0 bg-gray-100 rounded-lg sm:rounded-xl p-2 sm:p-4">
                <div className="flex flex-col items-center gap-4 sm:gap-6">
                  {selectedImages.map((img, idx) => (
                    <div
                      key={idx}
                      className="bg-white rounded-lg shadow-lg overflow-hidden w-full max-w-2xl"
                    >
                      {/* Page Header */}
                      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-600">Page {idx + 1}</span>
                        <span className="text-xs text-gray-400">{img.name}</span>
                      </div>
                      {/* Page Content - A4 Aspect Ratio */}
                      <div className="aspect-[3/4] bg-white p-2">
                        <img
                          src={URL.createObjectURL(img)}
                          alt={`Page ${idx + 1}`}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-3 sm:pt-4 border-t border-gray-100 mt-3 sm:mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
                  {selectedImages.length} pages ready
                </div>
                <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                  <Button variant="outline" onClick={() => setShowPdfPreview(false)} className="w-full sm:w-auto">
                    <ArrowLeft className="w-4 h-4 mr-2" /> <span className="hidden sm:inline">Back to Review</span><span className="sm:hidden">Back</span>
                  </Button>
                  <Button
                    onClick={async () => {
                      try {
                        setIsConvertingToPdf(true);
                        const selectedImgs = getSelectedImages();
                        const pdf = await convertImagesToPdf(selectedImgs);
                        onUploadPdf(pdf);
                        setShowPdfPreview(false);
                        onOpenChange(false);
                        toast.success("PDF ready for evaluation!");
                      } catch (err) {
                        console.error(err);
                        toast.error("Failed to create PDF");
                      } finally {
                        setIsConvertingToPdf(false);
                      }
                    }}
                    className="bg-indigo-600 text-white hover:bg-indigo-700 w-full sm:w-auto sm:min-w-[140px]"
                    disabled={isConvertingToPdf}
                  >
                    {isConvertingToPdf ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Creating...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        Upload PDF <FileText className="w-4 h-4" />
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImageReviewModal;
